/**
 * QuizLauncher.jsx — Bouton "Lancer le Quiz d'ancrage" en fin de cours.
 * Charge le quiz statique par quizId, crée la session backend, redirige le prof
 * vers la vue Host (QR code + stats live).
 */
import React, { useState } from 'react';
import { Button } from './ui/button';
import { Sparkles, Loader2 } from 'lucide-react';
import { quiz as quizJ1Am } from '../data/quiz/quizJournee1Am';
import { quiz as quizJ2Am } from '../data/quiz/quizJournee2Am';
import { quiz as quiz125Comp } from '../data/quiz/quiz125Comprendre';
import { quiz as quizSensib } from '../data/quiz/quizSensibilisation';
import { quiz as quizCeremonieAm } from '../data/quiz/quizCeremonieAm';

const QUIZ_MAP = {
  'permis-am-j1': quizJ1Am,
  'permis-am-j2': quizJ2Am,
  'formation-125-comprendre': quiz125Comp,
  'permis-am-sensibilisation': quizSensib,
  'ceremonie-am': quizCeremonieAm,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function QuizLauncher({ quizId, color = '#dc2626' }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const quiz = QUIZ_MAP[quizId];

  if (!quiz) return null;

  const start = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/quiz/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quiz_id: quiz.id,
          quiz_title: quiz.title,
          questions_count: quiz.questions.length,
          total_questions: quiz.questions,
        }),
      });
      if (!res.ok) throw new Error('Impossible de créer la session');
      const data = await res.json();
      // Navigation forcée (rechargement complet) pour éviter le bug SPA
      // qui laissait l'écran noir sur certains navigateurs de projection.
      window.location.href = `/quiz/host/${data.code}`;
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="mt-12 p-6 md:p-8 rounded-2xl border-2 text-center print:hidden"
      style={{ borderColor: `${color}55`, background: `linear-gradient(135deg, ${color}22, transparent)` }}
      data-testid="quiz-launcher"
    >
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-[10px] font-black uppercase tracking-[0.22em] mb-4" style={{ background: color }}>
        <Sparkles className="w-3 h-3" /> Ancrage · Fin de cours
      </div>
      <h3 className="text-xl md:text-2xl font-black text-foreground mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Quiz d'ancrage — {quiz.questions.length} questions
      </h3>
      <p className="text-sm text-muted-foreground max-w-2xl mx-auto mb-6">
        Lance une session live : tes élèves scannent le QR avec leur portable et leurs réponses s'affichent au tableau en temps réel.
      </p>
      <Button
        onClick={start}
        disabled={loading}
        size="lg"
        className="text-white font-black uppercase tracking-widest"
        style={{ background: color }}
        data-testid="quiz-launcher-btn"
      >
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Création…</> : <><Sparkles className="w-4 h-4 mr-2" /> Lancer le quiz</>}
      </Button>
      {error && <div className="mt-3 text-xs text-red-500">{error}</div>}
    </div>
  );
}

export default QuizLauncher;
